import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import {
  ArrowRight,
  Brain,
  Sparkles,
  Target,
  Activity,
  ShieldCheck,
  BarChart3,
  Zap,
} from "lucide-react";
import DisclaimerBanner from "../components/DisclaimerBanner";

// Per-category neon palette
const CATEGORY_META = {
  ability:     { Icon: Brain,      color: "#3B82F6", rgb: "59,130,246",   label_class: "neon-blue"    },
  personality: { Icon: Sparkles,   color: "#B026FF", rgb: "176,38,255",   label_class: "neon-purple"  },
  mental:      { Icon: Activity,   color: "#00FF94", rgb: "0,255,148",    label_class: "neon-green"   },
  aptitude:    { Icon: Target,     color: "#06B6D4", rgb: "6,182,212",    label_class: "neon-cyan"    },
  traits:      { Icon: BarChart3,  color: "#FF2DD0", rgb: "255,45,208",   label_class: "neon-magenta" },
};

const COLOR_MAP = {
  blue:   { color: "#3B82F6", rgb: "59,130,246",  cls: "neon-blue"   },
  purple: { color: "#B026FF", rgb: "176,38,255",  cls: "neon-purple" },
  green:  { color: "#00FF94", rgb: "0,255,148",   cls: "neon-green"  },
  cyan:   { color: "#06B6D4", rgb: "6,182,212",   cls: "neon-cyan"   },
};

export default function Landing() {
  const { t, tField } = useI18n();
  const nav = useNavigate();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content/landing").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  if (!content) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#070B14]">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading…</div>
      </div>
    );
  }

  const sb = content.status_bar;
  const hero = content.hero;
  const hud = content.hud;
  const catSec = content.categories_section;
  const proto = content.protocol_section;
  const fcta = content.final_cta;
  const categories = ["ability", "personality", "mental", "aptitude", "traits"];

  return (
    <div className="bg-[#070B14] text-white">
      {/* =========================  HERO  ========================= */}
      <section className="relative overflow-hidden border-b border-[#1E2A3F]">
        <div className="absolute inset-0 live-grid pointer-events-none" />
        <div className="absolute inset-0 hero-halo pointer-events-none" />
        <div
          className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(59,130,246,0.35), transparent 60%)", filter: "blur(40px)" }}
        />
        <div
          className="absolute -bottom-40 right-0 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(176,38,255,0.3), transparent 60%)", filter: "blur(60px)" }}
        />
        <div
          className="absolute top-1/3 right-1/3 w-[300px] h-[300px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,255,148,0.18), transparent 60%)", filter: "blur(50px)" }}
        />

        {/* status bar */}
        <div className="relative border-b border-[#1E2A3F] bg-[#0A0E1A]/40 backdrop-blur-sm">
          <div className="mx-auto max-w-7xl px-6 lg:px-10 h-9 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00FF94] pulse-dot" />
              <span className="text-[#00FF94]">{t("landing.status_online")}</span>
              <span className="hidden sm:inline">· {t("landing.status_subtitle")}</span>
            </div>
            <div className="hidden md:flex items-center gap-5">
              <span>{t("landing.status_tests")}</span>
              <span className="text-[#3B82F6]">{t("landing.status_langs")}</span>
              <span className="text-[#B026FF]">{t("landing.status_ai")}</span>
            </div>
          </div>
        </div>

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left */}
          <div className="lg:col-span-7 slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#3B82F6]/40 bg-[#3B82F6]/8 text-[#60A5FA] font-mono text-[10px] uppercase tracking-[0.25em] mb-8">
              <ShieldCheck className="h-3 w-3" strokeWidth={2} />
              {t("hero.disclaimer")}
            </div>

            <h1
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95]"
              style={{ fontWeight: 900 }}
            >
              <span className="block">{t("landing.title_line1")}</span>
              <span className="block gradient-text-multi">{t("landing.title_line2")}</span>
            </h1>

            <p className="mt-8 text-base lg:text-lg text-[#94A3B8] max-w-xl leading-relaxed">
              {t("hero.subtitle1")}
              <br />
              {t("hero.subtitle2")}
              <br />
              {t("hero.subtitle3")}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => nav("/catalog")}
                data-testid="hero-cta-primary"
                className="btn-gradient inline-flex items-center gap-2 text-white px-7 py-4 font-display text-sm font-bold tracking-wide rounded-md"
              >
                <Zap className="h-4 w-4" strokeWidth={2.5} />
                {t("hero.cta_primary")}
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
              <Link
                to="/catalog"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-[#1E2A3F] hover:border-[#00FF94] text-white hover:text-[#00FF94] px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] rounded-md transition-colors"
              >
                {t("hero.cta_secondary")}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {content.trust_stats.map((s, i) => {
                const c = COLOR_MAP[s.color] || COLOR_MAP.blue;
                const labelKeyMap = ["landing.trust_diagnostics", "landing.trust_languages", "landing.trust_per_test"];
                const lbl = labelKeyMap[i] ? t(labelKeyMap[i]) : tField({ ja: s.label_ja, en: s.label_en });
                return (
                  <div key={i}>
                    <div className={`font-mono text-2xl tracking-tight ${c.cls}`} style={{ fontWeight: 600 }}>
                      {s.value}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mt-1">
                      {lbl}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right HUD */}
          <div className="lg:col-span-5 relative slide-up" style={{ animationDelay: "120ms" }}>
            <div className="space-y-4">
              {/* Panel 1 — Score */}
              <div className="hud-panel p-5 relative overflow-hidden">
                <div className="scan-line" />
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                    {t("landing.hud_readout")}
                  </div>
                  <div className="font-mono text-[10px] text-[#00FF94]">{tField(hud.trend)}</div>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display tracking-tight neon-green" style={{ fontSize: 72, lineHeight: 0.85, fontWeight: 900 }}>
                    {tField(hud.score)}
                  </span>
                  <div className="flex flex-col">
                    <span className="font-mono text-xs text-[#94A3B8]">{t("landing.hud_score_unit")}</span>
                    <span className="font-mono text-[10px] text-[#3B82F6]">{t("landing.hud_band")}</span>
                  </div>
                </div>
                <div className="mt-3 h-1 bg-[#1E2A3F] overflow-hidden rounded-full">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: "100%",
                      background: "linear-gradient(90deg, #3B82F6, #B026FF, #00FF94)",
                      boxShadow: "0 0 12px rgba(0,255,148,0.5)",
                    }}
                  />
                </div>
              </div>

              {/* Panel 2 — Dimensions */}
              <div className="hud-panel p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
                    {t("landing.hud_dimensions")}
                  </div>
                  <div className="font-mono text-[10px] text-[#94A3B8]">{t("landing.hud_dimensions_status")}</div>
                </div>
                {[
                  { label: "LOGIC",   v: 92, color: "#3B82F6" },
                  { label: "NUMERIC", v: 88, color: "#B026FF" },
                  { label: "VERBAL",  v: 76, color: "#00FF94" },
                ].map((d) => (
                  <div key={d.label} className="mb-2 last:mb-0">
                    <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.18em] mb-1">
                      <span className="text-[#94A3B8]">{d.label}</span>
                      <span style={{ color: d.color }}>{d.v}</span>
                    </div>
                    <div className="h-1 bg-[#1E2A3F] overflow-hidden rounded-full">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${d.v}%`, background: d.color, boxShadow: `0 0 8px ${d.color}AA` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Panel 3 — SYS.LOG */}
              <div className="hud-panel p-4 font-mono text-[11px] leading-relaxed">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00FF94] pulse-dot" />
                  <span className="text-[#64748B] uppercase tracking-[0.2em] text-[9px]">{t("landing.hud_syslog")}</span>
                </div>
                {hud.syslog_lines.map((line, i) => (
                  <div key={i} style={{ color: ["#3B82F6", "#B026FF", "#00FF94"][i % 3] }}>
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================  CATEGORIES  ========================= */}
      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono mb-2">{t("landing.cat_eyebrow")}</div>
            <h2 className="font-display tracking-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)", lineHeight: 1.05, fontWeight: 800 }}>
              {t("landing.cat_title_pre")}
              <span className="gradient-text-multi">{t("landing.cat_title_highlight")}</span>
            </h2>
          </div>
          <Link
            to="/catalog"
            data-testid="landing-view-all-link"
            className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#3B82F6] hover:text-[#60A5FA] transition-colors"
          >
            {t("landing.view_all")}
            <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((c, i) => {
            const meta = CATEGORY_META[c];
            const Icon = meta.Icon;
            return (
              <Link
                key={c}
                to={`/catalog?cat=${c}`}
                data-testid={`landing-category-${c}`}
                className="group relative bg-[#0F1525] border border-[#1E2A3F] p-6 overflow-hidden card-hoverable rounded-md slide-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <span className="absolute top-0 left-0 w-3 h-3 border-t border-l opacity-0 group-hover:opacity-100 transition-opacity" style={{ borderColor: meta.color }} />
                <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r opacity-0 group-hover:opacity-100 transition-opacity" style={{ borderColor: meta.color }} />
                <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `radial-gradient(circle, rgba(${meta.rgb},0.25), transparent 70%)`, filter: "blur(20px)" }} />
                <div className="relative">
                  <div
                    className="h-11 w-11 flex items-center justify-center mb-4 rounded-md"
                    style={{
                      background: `rgba(${meta.rgb}, 0.15)`,
                      border: `1px solid rgba(${meta.rgb}, 0.4)`,
                      color: meta.color,
                      boxShadow: `0 0 14px rgba(${meta.rgb}, 0.25)`,
                    }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div className="font-display text-lg tracking-tight text-white" style={{ fontWeight: 800 }}>
                    {tField(content?.categories_labels?.[c]) || t(`categories.${c}`)}
                  </div>
                  <div className="font-mono text-[10px] mt-1 uppercase tracking-[0.2em]" style={{ color: meta.color, opacity: 0.7 }}>
                    {c}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* =========================  PROTOCOL  ========================= */}
      <section className="relative border-y border-[#1E2A3F] bg-[#0A0E1A] overflow-hidden">
        <div className="absolute inset-0 dot-bg opacity-50 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20">
          <div className="mb-12">
            <div className="label-mono mb-2">{t("landing.proto_eyebrow")}</div>
            <h2 className="font-display tracking-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)", lineHeight: 1.05, fontWeight: 800 }}>
              {t("landing.proto_title_pre")}
              <span className="gradient-text-multi">{t("landing.proto_title_highlight")}</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 relative">
            <div className="hidden lg:block absolute top-16 left-[16%] right-[16%] h-px pointer-events-none"
              style={{ background: "linear-gradient(90deg, transparent, #3B82F6 20%, #B026FF 50%, #00FF94 80%, transparent)", opacity: 0.6 }}
            />
            {proto.steps.map((s, i) => {
              const c = COLOR_MAP[s.color] || COLOR_MAP.blue;
              const stepTitleKey = `landing.step${i + 1}_title`;
              const stepBodyKey = `landing.step${i + 1}_body`;
              return (
                <div key={i} className="relative bg-[#0F1525] border border-[#1E2A3F] p-7 rounded-md transition-colors">
                  <div
                    className="inline-flex items-center justify-center w-14 h-14 mb-5 rounded-md font-display text-2xl"
                    style={{
                      background: `rgba(${c.rgb}, 0.12)`,
                      border: `1px solid rgba(${c.rgb}, 0.5)`,
                      color: c.color,
                      textShadow: `0 0 12px rgba(${c.rgb}, 0.7)`,
                      fontWeight: 900,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display text-xl tracking-tight text-white mb-2" style={{ fontWeight: 800 }}>
                    {t(stepTitleKey)}
                  </div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{t(stepBodyKey)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================  FINAL CTA  ========================= */}
      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="relative overflow-hidden bg-[#0F1525] border border-[#1E2A3F] rounded-md p-10 lg:p-14 text-center">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 50% 60% at 50% 0%, rgba(59,130,246,0.25), transparent 60%), radial-gradient(ellipse 50% 60% at 50% 100%, rgba(176,38,255,0.2), transparent 60%)",
            }}
          />
          <div className="relative">
            <div className="label-mono mb-3">{t("landing.final_eyebrow")}</div>
            <h2 className="font-display tracking-tight max-w-3xl mx-auto" style={{ fontSize: "clamp(1.75rem, 4vw, 3.5rem)", lineHeight: 1.05, fontWeight: 800 }}>
              {t("landing.final_title_pre")}
              <span className="gradient-text-multi">{t("landing.final_title_highlight")}</span>
            </h2>
            <p className="mt-5 text-[#94A3B8] max-w-xl mx-auto">{t("landing.final_subtitle")}</p>
            <button
              onClick={() => nav("/catalog")}
              data-testid="landing-final-cta"
              className="btn-gradient mt-8 inline-flex items-center gap-2 text-white px-8 py-4 font-display text-sm font-bold rounded-md"
            >
              <Zap className="h-4 w-4" strokeWidth={2.5} />
              {t("landing.final_button")}
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
        <div className="mt-10">
          <DisclaimerBanner />
        </div>
      </section>
    </div>
  );
}
